<template>
    <div class="subscribers-container">
        <div class="page-header">
            <h1 class="page-title">Contact Management</h1>

            <div class="action-buttons">
                <v-btn color="primary" class="action-btn" elevation="2" @click="openCreateDIalog()">
                    <v-icon left>mdi-account-plus</v-icon>
                    Add Contact
                </v-btn>

                <v-btn color="secondary" class="action-btn" elevation="2" @click="importDialog = true">
                    <v-icon left>mdi-file-import</v-icon>
                    Import from Excel
                </v-btn>

                <v-btn color="accent" class="action-btn" elevation="2" @click="sendCallToAllDialog = true">
                    <v-icon left>mdi-phone-outgoing</v-icon>
                    Call Everyone
                </v-btn>
            </div>
        </div>

        <div class="search-stats-bar">
            <div class="search-wrapper">
                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search by name or phone"
                    single-line hide-details filled rounded dense class="search-field" clearable></v-text-field>
            </div>

            <div class="stats-wrapper">
                <div class="stats-item">
                    <v-icon color="primary" class="stats-icon">mdi-account-group</v-icon>
                    <span class="stats-text">Total: <strong>{{ subscribes.length }}</strong> Contacts</span>
                </div>

                <!-- <v-btn color="info" class="export-btn" elevation="1" @click="exportSubscribers()">
                    <v-icon left>mdi-file-export</v-icon>
                    Export to Excel
                </v-btn> -->
            </div>
        </div>

        <div class="table-container">
            <v-card class="table-card">
                <v-data-table id="table1" :headers="headers" :items="subscribes" :search="search"
                    :loading="progressShow" :items-per-page="itemsPerPage" :footer-props="{
                        'items-per-page-options': [10, 20, 50, 100, 500, -1],
                        'items-per-page-text': 'Rows per page:',
                        'show-current-page': true,
                        'show-first-last-page': true
                    }" sort-by="createdAt" sort-desc class="subscribers-table" :mobile-breakpoint="0">
                    <template v-slot:progress>
                        <v-progress-linear color="primary" indeterminate height="3"></v-progress-linear>
                    </template>

                    <template v-slot:[`header.actions`]="{ header }">
                        <span class="table-header-text">Actions</span>
                    </template>

                    <template v-for="header in headers.filter(h => h.value !== 'actions')"
                        v-slot:[`header.${header.value}`]="{ header }">
                        <span class="table-header-text">{{ header.text }}</span>
                    </template>

                    <template v-slot:item="{ item, index }">
                        <tr class="table-row" :class="{ 'highlighted-row': index % 2 === 0 }">
                            <td class="text-center">{{ index + 1 }}</td>
                            <td>
                                <div class="name-cell">
                                    <v-avatar size="32" color="primary" class="white--text">
                                        {{ getInitials(item.name) }}
                                    </v-avatar>
                                    <span class="mr-2">{{ item.name || 'No Name' }}</span>
                                </div>
                            </td>
                            <td>
                                <div class="phone-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-phone</v-icon>
                                    {{ formatPhone(item.phone) }}
                                </div>
                            </td>
                            <td>{{ item.added_by }}</td>
                            <td>
                                <v-chip small :color="getRoleColor(item.role)" text-color="white" class="role-chip">
                                    {{ getRoleName(item.role) }}
                                </v-chip>
                            </td>
                            <td>
                                <div class="date-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-calendar</v-icon>
                                    {{ item.createdAt }}
                                </div>
                            </td>
                            <td>
                                <div class="date-cell">
                                    <v-icon small color="grey darken-1" class="ml-1">mdi-phone-in-talk</v-icon>
                                    {{ item.last_call || 'No Data' }}
                                </div>
                            </td>
                            <td>
                                <v-btn small text color="primary" @click="openSettingsDialog(item, index)"
                                    class="settings-btn">
                                    <v-icon small left>mdi-cog</v-icon>
                                    Settings
                                </v-btn>
                            </td>
                            <td>
                                <div class="actions-cell">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn :disabled="item.role === 'owner'" icon small color="info"
                                                v-bind="attrs" v-on="on" @click="openEditDialog(item, index)"
                                                class="action-icon-btn">
                                                <v-icon small>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Edit</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn :disabled="item.role === 'owner'" icon small color="error"
                                                v-bind="attrs" v-on="on" @click="openDeleteDialog(item, index)"
                                                class="action-icon-btn">
                                                <v-icon small>mdi-delete</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Delete</span>
                                    </v-tooltip>
                                </div>
                            </td>
                        </tr>
                    </template>

                    <template v-slot:no-data>
                        <div class="empty-state">
                            <v-icon size="64" color="grey lighten-1">mdi-account-off</v-icon>
                            <h3 class="empty-title">No Contacts</h3>
                            <p class="empty-subtitle">No contacts found in the system</p>
                            <v-btn color="primary" @click="refreshDetails()">
                                <v-icon left>mdi-refresh</v-icon>
                                Refresh Data
                            </v-btn>
                        </div>
                    </template>

                    <template v-slot:no-results>
                        <div class="empty-state">
                            <v-icon size="64" color="grey lighten-1">mdi-file-search</v-icon>
                            <h3 class="empty-title">No Results</h3>
                            <p class="empty-subtitle">No results found for "{{ search }}"</p>
                            <v-btn text color="primary" @click="search = ''">
                                Clear Search
                            </v-btn>
                        </div>
                    </template>
                </v-data-table>
            </v-card>
        </div>

        <ImportDialog v-model="importDialog" v-if="importDialog" @exportExampleExcel="exportExampleExcel"
            @checkDuplicate="checkDuplicate" @importedSuccessAdded="importedSuccessAdded" />

        <DeleteItemDialog v-model="deleteDialog" v-if="deleteDialog" :item="itemToDelete" @itemDeleted="itemDeleted" />

        <TzintukSettingsDialog v-model="settingsDialog" v-if="settingsDialog" :item="itemSettings"
            @itemSettingsChange="itemSettingsChange" />

        <SendCallToAllDialog v-model="sendCallToAllDialog" v-if="sendCallToAllDialog" />

        <AddEditDialog v-model="addEditDialog" v-if="addEditDialog" :formTitle="formTitle" :itemToEdit="editedItem"
            @subscribeCreated="newSubscribeSaved" @subscribeEdited="editedSubscribeSaved" />

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt" />
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'
import DeleteItemDialog from '@/components/subscribesList/dialogs/DeleteItemDialog'
import TzintukSettingsDialog from '@/components/subscribesList/dialogs/TzintukSettingsDialog'
import AddEditDialog from '@/components/subscribesList/dialogs/AddEditDialog'
import SendCallToAllDialog from '@/components/subscribesList/dialogs/SendCallToAllDialog'
import TableToExcel from "@linways/table-to-excel"
import * as XLSX from 'xlsx/xlsx.mjs'
import ImportDialog from '@/components/subscribesList/dialogs/ImportDialog'

export default {
    components: {
        SnackBar,
        DeleteItemDialog,
        AddEditDialog,
        ImportDialog,
        TzintukSettingsDialog,
        SendCallToAllDialog
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        lineNumber: "",
        progressShow: false,
        addEditDialog: false,
        settingsDialog: false,
        importDialog: false,
        importedFailed: [],
        importedSuccess: [],
        formTitle: "",
        itemToDelete: {},
        editedItem: {},
        itemSettings: {},
        deleteDialog: false,
        sendCallToAllDialog: false,
        search: "",
        subscribes: [],
        headers: [
            { text: '#', value: "index", sortable: false, width: '50px' },
            { text: 'Name', align: 'start', value: 'name' },
            { text: 'Phone', value: 'phone' },
            { text: 'Added By', value: 'added_by' },
            { text: 'Role', value: 'role' },
            { text: 'Joined Date', value: 'createdAt' },
            { text: 'Last Call', value: 'last_call' },
            { text: 'Call Record', value: 'record_name', width: '100px' },
            { text: 'Actions', value: 'actions', sortable: false, width: '120px' },
        ],
        itemsPerPage: 20,
        roleColors: {
            'manager': 'indigo',
            'user': 'teal',
            'owner': 'blue-grey',
            'default': 'red'
        },
        roleNames: {
            'manager': 'Manager',
            'user': 'User',
            'owner': 'Owner'
        }
    }),
    methods: {
        // Utility methods
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },

        formatPhone(phone) {
            if (!phone) return '';
            // Format as XXX-XXX-XXXX if 10 digits
            if (phone.length === 10) {
                return `${phone.substring(0, 3)}-${phone.substring(3, 6)}-${phone.substring(6)}`;
            }
            return phone;
        },

        getRoleColor(role) {
            return this.roleColors[role] || this.roleColors.default;
        },
        getRoleName(role) {
            return this.roleNames[role] || role;
        },
        // Data fetching
        async getSubscribes() {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/subscribes/get_subscribes_list`;

                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));

                if (!res.ok) {
                    throw new Error(`HTTP error! Status: ${res.status}`);
                }

                const jsonObject = await res.json();
                this.subscribes = jsonObject;
                this.formatDetails();
            } catch (error) {
                console.error("Error fetching subscribers:", error);
                this.showSnackBar(`Error loading data: ${error.message}`, "error");
            } finally {
                this.progressShow = false;
            }
        },

        formatDetails() {
            this.subscribes = this.subscribes.map((subscribe) => {
                return {
                    ...subscribe,
                    createdAt: utils.getCurrentDate(subscribe.createdAt),
                    last_call: subscribe.last_call ? utils.getCurrentDate(subscribe.last_call) : null,
                    added_by: subscribe.added_by === "WEBSITE" ? "Admin Panel" : subscribe.added_by
                };
            });
        },

        // UI interaction methods
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },

        openDeleteDialog(item, index) {
            this.itemToDelete = { ...item };
            this.deleteDialog = true;
        },

        openEditDialog(item, index) {
            this.formTitle = "Edit Contact";
            this.editedItem = { ...item, itemIndex: index };
            this.addEditDialog = true;
        },

        openSettingsDialog(item, index) {
            this.itemSettings = { ...item };
            this.settingsDialog = true;
        },

        openCreateDIalog() {
            this.formTitle = "Add New Contact";
            this.editedItem = {};
            this.addEditDialog = true;
        },

        // CRUD operations callbacks
        newSubscribeSaved(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                // Format the new item before adding to the list
                const formattedItem = {
                    ...item,
                    createdAt: utils.getCurrentDate(item.createdAt),
                    last_call: item.last_call ? utils.getCurrentDate(item.last_call) : null,
                    added_by: item.added_by === "WEBSITE" ? "Admin Panel" : item.added_by
                };

                this.subscribes = [formattedItem, ...this.subscribes];
            }
        },

        editedSubscribeSaved(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === item._id);
                if (index !== -1) {
                    // Format the edited item
                    const formattedItem = {
                        ...item,
                        createdAt: utils.getCurrentDate(item.createdAt),
                        last_call: item.last_call ? utils.getCurrentDate(item.last_call) : null,
                        added_by: item.added_by === "WEBSITE" ? "Admin Panel" : item.added_by
                    };

                    this.$set(this.subscribes, index, formattedItem);
                }
            }
        },

        itemDeleted(id, text, color) {
            this.showSnackBar(text, color);
            if (id) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === id);
                if (index !== -1) {
                    this.subscribes.splice(index, 1);
                }
            }
        },

        itemSettingsChange(item, text, color) {
            this.showSnackBar(text, color);
            if (item) {
                const index = this.subscribes.findIndex(subscribe => subscribe._id === item._id);
                if (index !== -1) {
                    this.$set(this.subscribes, index, { ...this.subscribes[index], ...item });
                }
            }
        },

        // Excel operations
        async exportExampleExcel() {
            try {
                // Create a workbook with a single worksheet
                const wb = XLSX.utils.book_new();

                // Sample data for the example
                const exampleData = [
                    { name: 'John Doe', phone: '0501234567', callerid_country_code: 'IL', recieve_calls: "Yes" },
                    { name: 'David Cohen', phone: '0521234567', callerid_country_code: 'IL', recieve_calls: "Yes" }
                ];

                // Convert the data to a worksheet
                const ws = XLSX.utils.json_to_sheet(exampleData);

                // Add the worksheet to the workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Example');

                // Generate the Excel file and trigger download
                XLSX.writeFile(wb, 'example_subscribers.xlsx');

                this.showSnackBar('Example file downloaded successfully', 'success');
            } catch (error) {
                console.error('Error generating example Excel:', error);
                this.showSnackBar('Error generating example file', 'error');
            }
        },

        async exportSubscribers() {
            try {
                this.progressShow = true;

                // Create a copy of the data for export, excluding unnecessary fields
                const exportData = this.subscribes.map((item, index) => ({
                    '#': index + 1,
                    'Name': item.name,
                    'Phone': item.phone,
                    'Added By': item.added_by,
                    'Role': item.role,
                    'Joined Date': item.createdAt,
                    'Last Call': item.last_call || 'No Data'
                }));

                // Create a workbook with a single worksheet
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(exportData);

                // Add the worksheet to the workbook
                XLSX.utils.book_append_sheet(wb, ws, 'Contacts');

                // Generate the Excel file and trigger download
                XLSX.writeFile(wb, `subscribers_${new Date().toISOString().split('T')[0]}.xlsx`);

                this.showSnackBar('Data exported successfully', 'success');
            } catch (error) {
                console.error('Error exporting subscribers:', error);
                this.showSnackBar('Error exporting data', 'error');
            } finally {
                this.progressShow = false;
            }
        },

        ImportExcel(file) {
            // Reset arrays for new import
            this.importedSuccess = [];
            this.importedFailed = [];

            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // Process each row
                    jsonData.forEach(row => {
                        let { name, phone, callerid_country_code, role } = row;

                        // Skip empty rows
                        if (!phone && !name) {
                            return;
                        }

                        // Validate phone number
                        if (!phone) {
                            this.importedFailed.push({ phone: 'Missing', reason: 'Missing phone number' });
                            return;
                        }

                        // Convert to string and format
                        phone = String(phone);

                        // Add leading zero if missing
                        if (!phone.startsWith("0")) {
                            phone = "0" + phone;
                        }

                        // Validate phone length
                        if (phone.length < 9) {
                            this.importedFailed.push({ phone, reason: 'Phone number too short' });
                            return;
                        }

                        // Validate phone format (numbers only)
                        const numberRegex = /^\d+$/;
                        if (!numberRegex.test(phone)) {
                            this.importedFailed.push({ phone, reason: 'Phone number contains invalid characters' });
                            return;
                        }

                        // Check for duplicates in existing data
                        const isDuplicate = this.subscribes.some(subscribe => subscribe.phone === phone);
                        if (isDuplicate) {
                            this.importedFailed.push({ phone, reason: 'Phone number already exists' });
                            return;
                        }

                        // Check for duplicates in current import
                        const isDuplicateInImport = this.importedSuccess.some(item => item.phone === phone);
                        if (isDuplicateInImport) {
                            this.importedFailed.push({ phone, reason: 'Phone number duplicates in file' });
                            return;
                        }

                        if (!role || role === 'owner') {
                            role = 'user';
                        }

                        // Add to success list
                        this.importedSuccess.push({
                            name: name || 'No Name',
                            phone,
                            role,
                            callerid_country_code: callerid_country_code || 'IL'
                        });
                    });

                    // Show import results dialog
                    this.importMessageDialog = true;

                } catch (error) {
                    console.error('Error parsing Excel file:', error);
                    this.showSnackBar('Error reading Excel file: ' + error.message, 'error');
                }
            };

            reader.onerror = (e) => {
                this.showSnackBar('Error reading file: ' + e.target.error, 'error');
            };

            reader.readAsArrayBuffer(file);
        },

        importedSuccessAdded() {
            this.showSnackBar(`${this.importedSuccess.length} contacts added successfully!`, 'success');
            this.getSubscribes();
        },

        refreshDetails() {
            this.getSubscribes();
        },
        // Check duplicate method
        checkDuplicate(phone, callback) {
            const isDuplicate = this.subscribes.some(subscribe => subscribe.phone === phone);
            callback(isDuplicate);
        },
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        this.getSubscribes();
    }
}
</script>

<style scoped>
/* Main Container */
.subscribers-container {
    padding: 24px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Header Section */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    flex-wrap: wrap;
    gap: 16px;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #0d2c6d;
    margin: 0;
}

.action-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.action-btn {
    font-weight: 500;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: 0;
    padding: 0 16px;
    height: 42px;
}

/* Search and Stats Bar */
.search-stats-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
}

.search-wrapper {
    width: 320px;
}

.search-field {
    border-radius: 8px;
}

.stats-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
}

.stats-item {
    display: flex;
    align-items: center;
    gap: 8px;
}

.stats-icon {
    opacity: 0.8;
}

.stats-text {
    font-size: 16px;
    color: #333;
}

.export-btn {
    text-transform: none;
    letter-spacing: 0;
    border-radius: 8px;
}

/* Table Container */
.table-container {
    margin-bottom: 32px;
}

.table-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
}

.subscribers-table {
    width: 100%;
}

/* Table Styling */
.table-header-text {
    font-weight: 600;
    font-size: 16px;
    color: #0d2c6d;
}

.table-row {
    height: 60px !important;
    transition: background-color 0.2s;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.highlighted-row {
    background-color: rgba(0, 0, 0, 0.02);
}

/* Cell Styling */
.name-cell {
    display: flex;
    align-items: center;
}

.phone-cell,
.date-cell {
    display: flex;
    align-items: center;
}

.role-chip {
    font-size: 12px;
    height: 24px;
}

.settings-btn {
    font-size: 12px;
    text-transform: none;
    letter-spacing: 0;
}

.actions-cell {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.action-icon-btn {
    margin: 0 4px;
}

/* Empty State */
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    text-align: center;
}

.empty-title {
    margin-top: 16px;
    font-size: 20px;
    font-weight: 600;
    color: #333;
}

.empty-subtitle {
    margin-top: 8px;
    margin-bottom: 24px;
    font-size: 16px;
    color: #666;
}

/* Responsive Styles */
@media (max-width: 960px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
    }

    .search-stats-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .search-wrapper {
        width: 100%;
    }

    .stats-wrapper {
        flex-direction: column;
        align-items: flex-start;
    }

    .action-buttons {
        width: 100%;
    }

    .action-btn {
        flex: 1;
    }
}

@media (max-width: 600px) {
    .subscribers-container {
        padding: 16px;
    }

    .page-title {
        font-size: 24px;
    }

    .action-buttons {
        flex-direction: column;
    }

    .action-btn {
        width: 100%;
    }
}
</style>